import type { HTMLAttributes, ReactNode } from "react";
import { AlertTriangle, RotateCw } from "lucide-react";
import { cn } from "../lib/cn";
import { Button } from "./Button";

export interface ErrorStateProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** O que falhou, em linguagem de gente. */
  title?: ReactNode;
  /** Causa provável + o que fazer. */
  description?: ReactNode;
  /** Código/id do erro para suporte. Sai em fonte mono, discreto. */
  detail?: string;
  onRetry?: () => void;
  retryLabel?: string;
  compact?: boolean;
}

/**
 * Erro é SEMPRE escopado ao componente que falhou: um widget quebrado não
 * troca a tela inteira por uma página de erro genérica (seção 6).
 * O vermelho fica contido no círculo do ícone (fundo `danger-soft`): o título
 * segue em tinta normal, para não gritar vermelho no meio do painel.
 */
export function ErrorState({
  title = "Não foi possível carregar",
  description = "Pode ser instabilidade momentânea na conexão com o servidor.",
  detail,
  onRetry,
  retryLabel = "Tentar novamente",
  compact = false,
  className,
  ...props
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center text-center",
        compact ? "gap-1.5 px-4 py-6" : "gap-1.5 px-6 py-10",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          "mb-1 inline-grid shrink-0 place-items-center rounded-full bg-danger-soft text-danger",
          compact ? "size-10" : "size-12",
        )}
      >
        <AlertTriangle className={compact ? "size-4" : "size-5"} />
      </span>
      <p className="text-md text-foreground">{title}</p>
      <p className="max-w-sm text-sm text-muted-foreground text-pretty">{description}</p>
      {detail ? (
        <p className="font-mono text-2xs text-subtle-foreground break-all">{detail}</p>
      ) : null}
      {onRetry ? (
        <div className="mt-2 max-md:w-full">
          <Button
            variant="secondary"
            size="sm"
            onClick={onRetry}
            leadingIcon={<RotateCw aria-hidden="true" className="size-3.5" />}
            className="max-md:w-full"
          >
            {retryLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
